package com.mps.asc_app.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilterChip
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.mps.asc_app.presentation.BookingViewModel
import com.mps.asc_app.ui.components.CourtSlotTiles
import com.mps.asc_app.ui.components.DayStrip
import com.mps.asc_app.ui.components.MonthCalendar
import com.mps.asc_app.ui.components.stripLength
import com.mps.asc_app.ui.components.SlotLegend
import com.mps.asc_app.ui.components.UiRow
import com.mps.asc_app.ui.components.UiSlot
import com.mps.asc_app.ui.components.UiSlotStatus
import com.mps.asc_app.ui.components.buildDayStrip
import com.mps.asc_app.ui.components.dayTitle
import com.mps.asc_app.ui.theme.AscNavy
import org.koin.compose.koinInject

private fun groupLabel(groupId: String): String {
    val raw = groupId.substringAfter(':', groupId)
    return raw.replace('-', ' ')
}

private fun trainingCommentForGroup(groupId: String): String {
    val label = groupLabel(groupId).trim()
    return if (label.isBlank()) "Training" else "$label Training"
}

/** Welches Blatt von unten gerade offen ist. */
private sealed interface BookingSheet {
    val slot: UiSlot
    data class Confirm(override val slot: UiSlot) : BookingSheet
    data class Admin(override val slot: UiSlot) : BookingSheet
    data class Trainer(override val slot: UiSlot) : BookingSheet
    data class Recurring(override val slot: UiSlot) : BookingSheet
}

/** "Platz 1 · 11:00 Uhr" für die Überschrift im Blatt. */
internal fun slotHeadline(slot: UiSlot, rows: List<UiRow>): String {
    val court = when (slot.courtId?.trim()?.lowercase()) {
        "court1" -> "Platz 1"
        "court2" -> "Platz 2"
        else -> slot.courtId?.takeIf { it.isNotBlank() }
    }
    val time = rows.firstOrNull { it.left.id == slot.id || it.right.id == slot.id }?.timeLabel
    return listOfNotNull(court, time?.let { "$it Uhr" }).joinToString(" · ").ifBlank { "Slot" }
}

/**
 * Buchungsseite (Redesign Stufe 2): Tagesstreifen statt Monatskalender, Plätze als große Kacheln,
 * Buchen und Trainer-/Admin-Aktionen als Blatt von unten. Kommende Buchungen stehen unter "Meine Spiele".
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun BookingScreen(
    modifier: Modifier = Modifier,
    contentPadding: PaddingValues = PaddingValues(),
    isAdmin: Boolean = false,
    isTrainer: Boolean = false,
    trainerAssignments: List<String> = emptyList(),
    courts: List<String> = listOf("Platz 1", "Platz 2"),
    onSlotClick: (UiSlot) -> Unit = {},
) {
    val bookingViewModel: BookingViewModel = koinInject()
    val uiState by bookingViewModel.state.collectAsState()

    var sheet by remember { mutableStateOf<BookingSheet?>(null) }
    var adminBlockComment by remember { mutableStateOf("") }
    var selectedTrainingGroupId by remember { mutableStateOf(trainerAssignments.firstOrNull().orEmpty()) }
    var recurringWeeksText by remember { mutableStateOf("12") }

    // Das ViewModel startet mit "heute" als gewähltem Tag; das merken wir uns als Start des Streifens.
    val today = remember { Triple(uiState.selectedDay, uiState.selectedMonth, uiState.selectedYear) }
    val days = remember(uiState.selectedDay, uiState.selectedMonth, uiState.selectedYear) {
        val (d, m, y) = today
        if (d == null || m == null || y == null) emptyList()
        else buildDayStrip(d, m, y, count = stripLength(d, m, y, uiState.selectedDay, uiState.selectedMonth, uiState.selectedYear))
    }
    var showCalendar by remember { mutableStateOf(false) }
    var calYear by remember { mutableStateOf(uiState.selectedYear ?: 2026) }
    var calMonth by remember { mutableStateOf(uiState.selectedMonth ?: 1) }

    LaunchedEffect(Unit) {
        bookingViewModel.loadConfig()
    }

    LaunchedEffect(isAdmin, isTrainer, trainerAssignments) {
        bookingViewModel.setViewerRole(
            isAdmin = isAdmin,
            isTrainer = isTrainer,
            trainerAssignments = trainerAssignments,
        )
        if (selectedTrainingGroupId.isBlank() || selectedTrainingGroupId !in trainerAssignments) {
            selectedTrainingGroupId = trainerAssignments.firstOrNull().orEmpty()
        }
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .padding(contentPadding)
            .verticalScroll(rememberScrollState())
    ) {
        // Blaues Band direkt unter der Kopfzeile mit dem Tagesstreifen.
        DayStrip(
            days = days,
            selectedDay = uiState.selectedDay,
            selectedMonth = uiState.selectedMonth,
            selectedYear = uiState.selectedYear,
            onSelect = { bookingViewModel.onDaySelected(it.day, it.month, it.year) },
            onOpenCalendar = {
                calYear = uiState.selectedYear ?: calYear
                calMonth = uiState.selectedMonth ?: calMonth
                showCalendar = true
            },
            modifier = Modifier
                .fillMaxWidth()
                .background(AscNavy)
                .padding(top = 4.dp, bottom = 16.dp),
        )

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .widthIn(max = 720.dp)
                .align(Alignment.CenterHorizontally)
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            val d = uiState.selectedDay
            val m = uiState.selectedMonth
            val y = uiState.selectedYear
            if (d != null && m != null && y != null) {
                Text(text = dayTitle(d, m, y), style = MaterialTheme.typography.titleLarge, color = AscNavy)
            }
            SlotLegend()

            uiState.error?.let { message ->
                Text(text = message, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodyMedium)
            }

            if (uiState.rows.isEmpty() && !uiState.isLoading) {
                Text(
                    text = "Für diesen Tag gibt es keine buchbaren Zeiten.",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }

            CourtSlotTiles(
                courts = courts,
                rows = uiState.rows,
                onSlotClick = { slot ->
                    when {
                        isAdmin && slot.status == UiSlotStatus.FREE -> {
                            adminBlockComment = ""
                            sheet = BookingSheet.Admin(slot)
                        }
                        !isAdmin && isTrainer && slot.status == UiSlotStatus.FREE -> sheet = BookingSheet.Trainer(slot)
                        // Erneutes Tippen auf einen vom Admin gesperrten Slot entsperrt ihn.
                        isAdmin && slot.status == UiSlotStatus.BLOCKED && slot.isAdminBlocked -> bookingViewModel.unblockSlot(slot)
                        slot.status == UiSlotStatus.FREE -> sheet = BookingSheet.Confirm(slot)
                        else -> bookingViewModel.onSlotClicked(slot)
                    }
                    onSlotClick(slot)
                },
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }

    if (showCalendar) {
        ModalBottomSheet(
            onDismissRequest = { showCalendar = false },
            sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true),
            containerColor = MaterialTheme.colorScheme.surface,
        ) {
            MonthCalendar(
                year = calYear,
                month = calMonth,
                selectedDay = uiState.selectedDay.takeIf { uiState.selectedMonth == calMonth && uiState.selectedYear == calYear },
                onPrevMonth = { if (calMonth == 1) { calMonth = 12; calYear -= 1 } else calMonth -= 1 },
                onNextMonth = { if (calMonth == 12) { calMonth = 1; calYear += 1 } else calMonth += 1 },
                onDayClick = { day ->
                    bookingViewModel.onDaySelected(day, calMonth, calYear)
                    showCalendar = false
                },
                modifier = Modifier.fillMaxWidth().navigationBarsPadding().padding(start = 16.dp, end = 16.dp, bottom = 24.dp),
            )
        }
    }

    val current = sheet ?: return
    val headline = slotHeadline(current.slot, uiState.rows)
    ModalBottomSheet(
        onDismissRequest = { sheet = null },
        sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true),
        containerColor = MaterialTheme.colorScheme.surface,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .navigationBarsPadding()
                .padding(start = 20.dp, end = 20.dp, bottom = 24.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            when (current) {
                is BookingSheet.Confirm -> {
                    SheetHeader(headline, "60 Minuten")
                    Text(
                        "Du kannst bis zu 3 zukünftige Buchungen haben. Stornieren geht unter „Meine Spiele“.",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                    PrimarySheetButton("Platz buchen") {
                        bookingViewModel.onSlotClicked(current.slot)
                        sheet = null
                    }
                    SecondarySheetButton("Abbrechen") { sheet = null }
                }

                is BookingSheet.Admin -> {
                    SheetHeader(headline, "Als Admin buchen oder sperren")
                    if (isTrainer) TrainingGroupChips(trainerAssignments, selectedTrainingGroupId) { selectedTrainingGroupId = it }
                    OutlinedTextField(
                        value = adminBlockComment,
                        onValueChange = { adminBlockComment = it },
                        label = { Text("Kommentar zum Sperren (optional)") },
                        modifier = Modifier.fillMaxWidth(),
                    )
                    PrimarySheetButton("Selbst buchen") {
                        bookingViewModel.onSlotClicked(current.slot)
                        sheet = null
                    }
                    SecondarySheetButton("Sperren") {
                        bookingViewModel.blockSlot(current.slot, adminBlockComment)
                        sheet = null
                    }
                    if (isTrainer) TrainingButtons(current.slot, selectedTrainingGroupId, bookingViewModel) { sheet = it }
                    else TextButton(onClick = { sheet = null }, modifier = Modifier.fillMaxWidth()) { Text("Abbrechen") }
                }

                is BookingSheet.Trainer -> {
                    SheetHeader(headline, "Wie möchtest du buchen?")
                    TrainingGroupChips(trainerAssignments, selectedTrainingGroupId) { selectedTrainingGroupId = it }
                    PrimarySheetButton("Selbst buchen") {
                        bookingViewModel.onSlotClicked(current.slot)
                        sheet = null
                    }
                    TrainingButtons(current.slot, selectedTrainingGroupId, bookingViewModel) { sheet = it }
                }

                is BookingSheet.Recurring -> {
                    val recurringComment = trainingCommentForGroup(selectedTrainingGroupId)
                    SheetHeader(headline, "Regeltermin: jede Woche denselben Slot sperren")
                    OutlinedTextField(
                        value = recurringWeeksText,
                        onValueChange = { recurringWeeksText = it.filter { ch -> ch.isDigit() } },
                        label = { Text("Anzahl Wochen (1–52)") },
                        modifier = Modifier.fillMaxWidth(),
                    )
                    Text("Kommentar: $recurringComment", style = MaterialTheme.typography.bodyMedium)
                    PrimarySheetButton("Anlegen") {
                        val weeks = recurringWeeksText.toIntOrNull() ?: 12
                        bookingViewModel.createRecurringTrainingBlocks(
                            slot = current.slot,
                            trainingGroupId = selectedTrainingGroupId,
                            weeks = weeks,
                            comment = recurringComment,
                        )
                        sheet = null
                    }
                    SecondarySheetButton("Abbrechen") { sheet = null }
                }
            }
        }
    }
}

@Composable
private fun SheetHeader(headline: String, subtitle: String) {
    Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text(text = headline, style = MaterialTheme.typography.headlineSmall, color = AscNavy)
        Text(subtitle, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
    }
}

@Composable
private fun ColumnScope.TrainingGroupChips(groups: List<String>, selected: String, onSelect: (String) -> Unit) {
    Text("Mannschaft/Jahrgang", style = MaterialTheme.typography.labelLarge)
    if (groups.isEmpty()) {
        Text("Dir ist keine Mannschaft/kein Jahrgang zugewiesen.", color = MaterialTheme.colorScheme.error)
        return
    }
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        groups.forEach { groupId ->
            FilterChip(selected = selected == groupId, onClick = { onSelect(groupId) }, label = { Text(groupLabel(groupId)) })
        }
    }
}

@Composable
private fun TrainingButtons(slot: UiSlot, groupId: String, vm: BookingViewModel, onSheet: (BookingSheet?) -> Unit) {
    SecondarySheetButton("Als Training buchen", enabled = groupId.isNotBlank()) {
        vm.bookTrainingSlot(slot, groupId)
        onSheet(null)
    }
    SecondarySheetButton("Als Regeltermin (wöchentlich)", enabled = groupId.isNotBlank()) {
        onSheet(BookingSheet.Recurring(slot))
    }
    TextButton(onClick = { onSheet(null) }, modifier = Modifier.fillMaxWidth()) { Text("Abbrechen") }
}

@Composable
private fun PrimarySheetButton(label: String, enabled: Boolean = true, onClick: () -> Unit) {
    Button(onClick = onClick, enabled = enabled, modifier = Modifier.fillMaxWidth().heightIn(min = 52.dp)) { Text(label) }
}

@Composable
private fun SecondarySheetButton(label: String, enabled: Boolean = true, onClick: () -> Unit) {
    OutlinedButton(onClick = onClick, enabled = enabled, modifier = Modifier.fillMaxWidth().heightIn(min = 52.dp)) { Text(label) }
}
